import { useState, useEffect, useRef } from 'react';
import {
  TALENT_PLACEHOLDER_SQUARE, PRODUCER_PLACEHOLDER, IMAGE_PLACEHOLDER,
} from '../utils/profilePlaceholders';
import { resizedImageUrl } from '../utils/imageUrl';

/** 서비스 공통 로딩 모션 (LoadingSpinner와 동일한 파일을 재사용해 캐시를 공유한다) */
const LOADING_GIF = '/assets/images/Castar_web_Loading_motion_black_v2.gif';

/**
 * 캐시된 이미지까지 스피너를 번쩍이면 오히려 산만하므로,
 * 이 시간 안에 로드가 끝나면 스켈레톤만 보여주고 모션은 띄우지 않는다.
 */
const SPINNER_DELAY_MS = 300;

/**
 * 기본 이미지는 두 종류다.
 *   cover   — 칸을 꽉 채우도록 그려둔 실루엣 (배우 카드)
 *   contain — 24×24 라인 아이콘. 늘리면 뭉개지므로 가운데에 작게 놓는다
 */
const PLACEHOLDER_BY_KIND = {
  talent: { src: TALENT_PLACEHOLDER_SQUARE, fit: 'cover' },
  profile: { src: TALENT_PLACEHOLDER_SQUARE, fit: 'cover' },
  company: { src: PRODUCER_PLACEHOLDER, fit: 'contain' },
  producer: { src: PRODUCER_PLACEHOLDER, fit: 'contain' },
  image: { src: IMAGE_PLACEHOLDER, fit: 'contain' },
};

/** 종류 키워드 · { src, fit } 객체 · 직접 넘긴 이미지 URL 모두 받는다 */
const resolvePlaceholder = (kind) => {
  if (!kind) return null;
  if (typeof kind === 'object') return kind;
  return PLACEHOLDER_BY_KIND[kind] ?? { src: kind, fit: 'cover' };
};

/**
 * @component FadeImage
 * @description 로딩 모션 + fade-in + 기본 이미지가 적용된 이미지 컴포넌트
 *
 * - 로딩 중: 회색 배경 위에 캐스타 로딩 모션 (300ms 이상 걸릴 때만)
 * - 로드 완료: fade-in 전환
 * - 실패/이미지 없음: 종류에 맞는 기본 아이콘을 원본 비율 그대로 가운데 표시
 *   (24×24 라인 아이콘을 cover로 늘리면 뭉개지므로 contain + 크기 제한)
 * - onError: src → originalUrl → fallback → 기본 아이콘 순으로 한 방향으로만 물러난다
 *
 * @param {string}  [src]         - 원본 이미지 URL (없으면 곧바로 기본 아이콘)
 * @param {string}  alt           - 대체 텍스트
 * @param {'thumb'|'medium'} [size] - 리사이즈본을 먼저 시도한다.
 *   thumb 300px(아바타·로고), medium 800px(카드·상세). 생략하면 원본을 그대로 쓴다.
 * @param {string}  [originalUrl] - 리사이즈 실패 시 시도할 원본 URL
 * @param {string}  [fallback]    - 그다음 시도할 대체 이미지 URL
 * @param {string|object} [placeholder] - 'talent' | 'company' | 'image' | 직접 URL | { src, fit } | null
 * @param {string}  [background]  - 스켈레톤 배경색
 */
function FadeImage({
  src,
  alt,
  size,
  originalUrl,
  fallback,
  placeholder = 'image',
  background = '#E9E9EC',
  style,
  className,
  ...rest
}) {
  const placeholderSrc = resolvePlaceholder(placeholder);

  // 시도 순서를 목록으로 고정한다. '지금 URL과 다르면 바꾼다' 식으로 비교하면
  // 마지막 후보가 실패했을 때 앞 후보로 되돌아가 무한히 왕복한다.
  // 리사이즈본이 아직 안 만들어졌을 수 있으므로 원본을 바로 뒤에 둔다
  const candidates = [size ? resizedImageUrl(src, size) : null, src, originalUrl, fallback]
    .filter(Boolean)
    .filter((url, i, all) => all.indexOf(url) === i);

  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(candidates.length ? 'loading' : 'empty');
  const [showSpinner, setShowSpinner] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setStep(0);
    setStatus(src || originalUrl || fallback ? 'loading' : 'empty');
  }, [src, size, originalUrl, fallback]);

  useEffect(() => {
    if (status !== 'loading') {
      setShowSpinner(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, step]);

  const attempt = candidates[step] ?? null;

  /**
   * 캐시에 있던 이미지는 React가 onLoad를 붙이기 전에 로드가 끝나 이벤트를 놓친다.
   * 그대로 두면 이미 그려진 이미지 위에 로딩 모션이 영원히 남으므로,
   * 렌더 직후 실제 상태를 직접 확인해 맞춰준다.
   */
  useEffect(() => {
    const img = imgRef.current;
    if (!img || status !== 'loading' || !img.complete) return;
    if (img.naturalWidth > 0) setStatus('loaded');
    else if (step + 1 < candidates.length) setStep(step + 1);
    else setStatus('failed');
  }, [status, step, attempt, candidates.length]);

  const handleError = () => {
    if (step + 1 < candidates.length) setStep(step + 1);
    else setStatus('failed');
  };

  const showPlaceholder = placeholderSrc && (status === 'empty' || status === 'failed');

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {attempt && (
        <img
          ref={imgRef}
          src={attempt}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setStatus('loaded')}
          onError={handleError}
          className={className}
          data-testid="fade-image"
          data-status={status}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: status === 'failed' ? 'none' : 'block',
            opacity: status === 'loaded' ? 1 : 0,
            transition: 'opacity 0.4s ease',
            ...style,
          }}
          {...rest}
        />
      )}

      {showSpinner && status === 'loading' && (
        <img
          src={LOADING_GIF}
          alt="로딩 중"
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: '46%',
            maxWidth: 64,
            minWidth: 22,
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      )}

      {showPlaceholder && (
        <img
          src={placeholderSrc.src}
          alt={status === 'empty' ? alt || '' : ''}
          data-testid="fade-image-placeholder"
          style={placeholderSrc.fit === 'cover'
            ? {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
            }
            : {
              position: 'absolute',
              width: '36%',
              maxWidth: 56,
              minWidth: 20,
              objectFit: 'contain',
              opacity: 0.85,
              pointerEvents: 'none',
            }}
        />
      )}
    </div>
  );
}

export default FadeImage;
